import {
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import axios from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { DisplayDateTime } from "components/DisplayDate";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getNotificationDestination } from "utils/notificationNavigation";
import { setNotificationRead } from "utils/notificationReadState";
import { getMrtBodyRowSx } from "theme/mrtTableTheme";
import { hasPermission } from "utils/permissionUtils";
import { P } from "const/permissions";
import ConfirmDialog from "components/Modals/ConfirmDialog";

const isNotificationUnread = (item) => {
  const value = item?.read ?? item?.isRead;
  return (
    value === false ||
    value === 0 ||
    value === "N" ||
    value === "n" ||
    value === "false" ||
    value === "UNREAD"
  );
};

const Notifications = () => {
  const canUpdateNotifications = hasPermission(P.NOTIFICATIONS.UPDATE);
  const canDeleteNotifications = hasPermission(P.NOTIFICATIONS.DELETE);
  const [
    { notificationsList, isNotificationLoading, notificationPagination },
    {
      setNotificationPagination: setPagination,
      refetchNotifications,
      refetchNotificationsForNavbar,
      setIsNotificationsPageActive,
    },
  ] = useKothar();
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, id: null });
  const [selectedDeleteCount, setSelectedDeleteCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setIsNotificationsPageActive(true);
    return () => setIsNotificationsPageActive(false);
  }, [setIsNotificationsPageActive]);
  const handleClickNotification = (item) => {
    if (!item?.read && canUpdateNotifications) {
      handleMarkAsRead("single", item?.id);
    }
    // if (item?.module?.includes("task")) {
    //   navigate(`/admin/tasks/view/${item?.taskId}`);
    // } else if (item?.module === "student" && item?.topicId) {
    //   navigate(`/admin/admission/edit/${item.topicId}`, { state: { tab: 4 } });
    // } else if (item?.module === "account" && item?.clientId) {
    //   navigate(`/admin/account/add`, { state: { item: { clientAccountId: item.clientId, hasAccountDetails: true } } });
    // } else if (item?.module?.includes("client")) {
    //   navigate(`/admin/client/view/${item?.clientId}?tab=notes`);

    const dest = getNotificationDestination(item);
    if (dest) {
      navigate(dest.path, dest.state ? { state: dest.state } : undefined);
    }
  };

  const handleMarkAsRead = useCallback(async (type, id) => {
    let notificationIds;
    let url;
    if (type === "single") {
      url = `/notification/${id}/mark-as-read`;
    } else {
      notificationIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
      url = `/notification/mark-as-read`;
    }
    try {
      setLoading(true);
      await axios.put(
        `${API_URL}${url}`,
        { notificationIds },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Notifications marked as read");
      // setNotificationTriggered(!notificationTriggered);
      table.resetRowSelection();
      refetchNotifications(); // No params needed - uses current pagination
      refetchNotificationsForNavbar();
    } catch (error) {
      toast.error("Failed to mark notifications as read");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleMarkAsUnread = useCallback(
    async (id) => {
      try {
        setLoading(true);
        await setNotificationRead(id, false);
        toast.success("Notification marked as unread");
        refetchNotifications();
        refetchNotificationsForNavbar();
      } catch (error) {
        toast.error("Failed to mark notification as unread");
      } finally {
        setLoading(false);
      }
    },
    [refetchNotifications, refetchNotificationsForNavbar]
  );

  const openDeleteConfirm = useCallback((type, id, count = 1) => {
    setSelectedDeleteCount(count);
    setDeleteConfirm({ open: true, type, id: id ?? null });
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirm({ open: false, type: null, id: null });
    setSelectedDeleteCount(0);
  }, []);

  const executeDelete = useCallback(async () => {
    const { type, id } = deleteConfirm;
    const notificationIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
    const url =
      type === "single" ? `${API_URL}/notification/${id}` : `${API_URL}/notification/multiple`;

    try {
      setLoading(true);
      await axios.delete(url, {
        ...(type === "multiple" ? { data: { id: notificationIds } } : {}),
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Notifications deleted successfully");
      table.resetRowSelection();
      refetchNotifications();
      refetchNotificationsForNavbar();
      closeDeleteConfirm();
    } catch (error) {
      toast.error(error?.response?.data?.errorMessage || "Failed to delete notifications");
    } finally {
      setLoading(false);
    }
  }, [deleteConfirm, closeDeleteConfirm, refetchNotifications, refetchNotificationsForNavbar]);

  const handleDelete = useCallback(
    (type, id) => {
      openDeleteConfirm(type, id);
    },
    [openDeleteConfirm]
  );

  const handleRefresh = () => {
    refetchNotifications();
    refetchNotificationsForNavbar();
  };

  const handleFilterChange = (filterType, value) => {
    setPagination((prev) => ({
      ...prev,
      [filterType]: value,
      pageIndex: 0, // Reset to first page when filters change
    }));
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "content",
        header: "Content",
        size: 300,
        maxSize: 400,
        minSize: 200,
        Cell: ({ row }) => (
          <div
            className={`max-w-[700px] whitespace-pre-wrap break-words ${
              isNotificationUnread(row.original) ? "font-semibold" : ""
            }`}
          >
            {row.original.content}
          </div>
        ),
      },
      {
        accessorKey: "date",
        header: "Date",
        size: 200,
        Cell: ({ row }) => <DisplayDateTime value={row.original.date} />,
      },
      {
        accessorKey: "action",
        header: "Action",
        size: 150,
        Cell: ({ row }) => (
          <Box className="flex gap-2">
            {canUpdateNotifications &&
              (!row.original.read ? (
                <Tooltip title="Mark as read">
                  <IconButton
                    onClick={() => handleMarkAsRead("single", row.original.id)}
                    disabled={loading}
                  >
                    <CheckCircleIcon color="primary" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Mark as unread">
                  <IconButton
                    onClick={() => handleMarkAsUnread(row.original.id)}
                    disabled={loading}
                  >
                    <CheckCircleIcon color="disabled" />
                  </IconButton>
                </Tooltip>
              ))}
            {canDeleteNotifications && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete("single", row.original.id)}
                  disabled={loading}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [
      notificationsList,
      canUpdateNotifications,
      canDeleteNotifications,
      loading,
      handleMarkAsRead,
      handleMarkAsUnread,
      openDeleteConfirm,
    ]
  );

  const table = useMaterialReactTable({
    columns,
    data: notificationsList?.content || [],
    rowNumberDisplayMode: "original",
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [20, 50, 100, 200, 400],
    },
    paginationDisplayMode: "pages",

    rowCount: notificationsList?.totalItems,
    pageCount: Math.ceil(notificationsList?.totalPages || 0),
    onPaginationChange: setPagination,
    state: {
      isLoading: isNotificationLoading || loading,
      isSaved: isNotificationLoading || loading,
      showProgressBars: isNotificationLoading || loading,
      pagination: notificationPagination,
    },
    enablePagination: true,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableColumnPinning: true,
    enableStickyFooter: true,
    manualPagination: true, // Enable manual pagination
    muiSkeletonProps: {
      animation: "wave",
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 300px)", // Adjust this value based on your header height
        minHeight: "calc(100vh - 300px)", // Set a minimum height
        overflowY: "auto",
        position: "relative", // Important for sticky positioning
      },
    },

    positionToolbarAlertBanner: "bottom",
    muiTableBodyRowProps: ({ row }) => {
      const isUnread = isNotificationUnread(row.original);
      const stripeBg = row.index % 2 === 0 ? "var(--app-surface)" : "var(--app-table-row-alt)";

      return {
        "data-unread": isUnread ? "true" : "false",
        onClick: (event) => {
          const isActionClick =
            event.target.closest(".MuiIconButton-root") || event.target.closest(".MuiPopover-root");
          if (!isActionClick) {
            handleClickNotification(row.original);
          }
        },
        sx: (theme) => {
          const unreadBg = alpha(theme.palette.primary.main, 0.12);
          const unreadHoverBg = alpha(theme.palette.primary.main, 0.2);
          const rowBg = isUnread ? unreadBg : stripeBg;

          return {
            ...getMrtBodyRowSx(row, { backgroundColor: rowBg }),
            ...(isUnread
              ? {
                  boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.main}`,
                  "& .MuiTableCell-root": {
                    backgroundColor: `${unreadBg} !important`,
                    color: "var(--app-text)",
                  },
                  "&:hover": {
                    "&, &:nth-of-type(even), &:nth-of-type(odd)": {
                      backgroundColor: unreadHoverBg,
                    },
                    "& .MuiTableCell-root": {
                      backgroundColor: `${unreadHoverBg} !important`,
                    },
                  },
                }
              : {}),
          };
        },
      };
    },
    muiTableBodyCellProps: ({ row }) => {
      const isUnread = isNotificationUnread(row.original);
      return {
        sx: (theme) =>
          isUnread
            ? {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              }
            : {},
      };
    },
    renderTopToolbarCustomActions: () => (
      <div className="flex gap-2 justify-start items-center ml-4">
        {/* Filters Section */}
        <Box className="flex flex-wrap gap-4 items-center">
          {/* Read Status Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={notificationPagination?.readEnum || "ALL"}
              label="Status"
              onChange={(e) => handleFilterChange("readEnum", e.target.value)}
              disabled={loading}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="READ">Read</MenuItem>
              <MenuItem value="UNREAD">Unread</MenuItem>
            </Select>
          </FormControl>

          {/* Tag Filter */}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Tag</InputLabel>
            <Select
              value={notificationPagination?.tag || ""}
              label="Tag"
              onChange={(e) => handleFilterChange("tag", e.target.value)}
              disabled={loading}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NORMAL">Normal</MenuItem>
              <MenuItem value="DOCUMENT_EXPIRY">Document Expiry</MenuItem>
              <MenuItem value="EVENT">Event</MenuItem>
              <MenuItem value="FOLLOW_UP">Follow Up</MenuItem>
              <MenuItem value="TASK">Task</MenuItem>
              <MenuItem value="REMINDER">Reminder</MenuItem>
              <MenuItem value="PAYMENT">Payment</MenuItem>
              <MenuItem value="OLD">Old</MenuItem>
            </Select>
          </FormControl>

          {/* Sort By Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={notificationPagination?.sortBy || "createdDate"}
              label="Sort By"
              onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              disabled={loading}
            >
              <MenuItem value="createdDate">Date</MenuItem>
              <MenuItem value="content">Content</MenuItem>
              <MenuItem value="read">Read Status</MenuItem>
            </Select>
          </FormControl>

          {/* Sort Direction Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Direction</InputLabel>
            <Select
              value={notificationPagination?.sortDirection || "DESC"}
              label="Direction"
              onChange={(e) => handleFilterChange("sortDirection", e.target.value)}
              disabled={loading}
            >
              <MenuItem value="ASC">Ascending</MenuItem>
              <MenuItem value="DESC">Descending</MenuItem>
            </Select>
          </FormControl>

          {canUpdateNotifications && (
            <Button
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleMarkAsRead("multiple")}
              disabled={!table.getSelectedRowModel().rows.length || loading}
            >
              Mark as Read
            </Button>
          )}
          {canDeleteNotifications && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() =>
                openDeleteConfirm("multiple", null, table.getSelectedRowModel().rows.length)
              }
              disabled={!table.getSelectedRowModel().rows.length || loading}
            >
              Delete
            </Button>
          )}
        </Box>
      </div>
    ),
  });

  const deleteDialogMessage =
    deleteConfirm.type === "multiple"
      ? `Are you sure you want to delete ${selectedDeleteCount} selected notification${
          selectedDeleteCount === 1 ? "" : "s"
        }?`
      : "Are you sure you want to delete this notification?";

  return (
    <div className="flex flex-col h-screen overflow-y-hidden">
      <div className="w-full px-4 py-4 overflow-y-hidden">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text h-full"
          }
        >
          <div className="flex flex-col gap-4 mb-0 p-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-app-text">Notifications</h2>
                <div className="flex items-center gap-2">
                  {/* Total Count */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-app-text-secondary">Total:</span>
                    <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-semibold text-app-text bg-app-surface-muted rounded-full min-w-[32px] h-7">
                      {notificationsList?.totalItems || 0}
                    </span>
                  </div>
                  {/* Unread Count */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-app-text-secondary">Unread:</span>
                    {notificationsList?.unreadCount > 0 ? (
                      <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-semibold text-white bg-red-500 rounded-full min-w-[32px] h-7">
                        {notificationsList.unreadCount}
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-semibold text-app-text-secondary bg-app-surface-hover rounded-full min-w-[32px] h-7">
                        0
                      </span>
                    )}
                  </div>
                </div>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading}
                    className="hover:bg-app-surface-muted"
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete notification"
        message={deleteDialogMessage}
        confirmLabel="Delete"
        confirmColor="error"
        loading={loading}
        onConfirm={executeDelete}
        onCancel={closeDeleteConfirm}
      />
    </div>
  );
};

export default Notifications;
